import { Flex, VStack, HStack, Icon, Center, Text } from '@chakra-ui/react'
import { usePCProject } from 'api/pc-projects'

import { Card } from 'components/card/card'
import { BlankSlate } from 'components/skeletons/skeleton-unit'
import numeral from 'numeral'
import React from 'react'

export const FinancialOverview: React.FC<{ ticketId: any }> = ({ ticketId }): JSX.Element => {
  const { projectData, isLoading } = usePCProject(ticketId)

  const finalSOW = (projectData as any)?.approvedSow
  const vendorCost = (projectData as any)?.vendorCost
  const material = (projectData as any)?.material
  const profit = (projectData as any)?.profit
  const changeOrder = (projectData as any)?.changeOrder
  const margin = (projectData as any)?.profitMargin

  return (
    <>
      <Card
        py="10px"
        px="0"
        boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
        borderRadius="6px"
        border="1px solid #CBD5E0"
        margin="0"
      >
        <Flex flexWrap="wrap" w="100%">
          <FiguresCard title="Approved SOW" value={finalSOW} isLoading={isLoading} />

          <FiguresCard
            title="Vendor Cost"
            value={vendorCost}
            icon={VendorCostIcon}
            border={true}
            isLoading={isLoading}
          />

          <FiguresCard
            title="Change Order"
            value={changeOrder}
            border={true}
            isLoading={isLoading}
            icon={ChangeOrderIcon}
          />

          <FiguresCard title="Material" value={material} border={true} isLoading={isLoading} icon={MaterialIcon} />

          <FiguresCard title="Profit" value={profit} border={true} isLoading={isLoading} icon={ProfitIcon} />

          <FiguresCard
            title="Margin"
            value={margin}
            icon={ProfitMarginIcon}
            border={true}
            isLoading={isLoading}
            type="info"
          />
        </Flex>
      </Card>
    </>
  )
}

type FiguresProps = {
  title: string
  value: string
  icon?: any
  border?: boolean
  isLoading?: boolean
  type?: string
}

const FiguresCard = (props: FiguresProps) => {
  const icon = props.icon ? props.icon : DollarIcon
  return (
    <>
      <VStack w="16.6%" {...(props.border ? { borderLeft: '1px solid #CBD5E0' } : {})}>
        <HStack>
          <Icon as={icon} color="#345EA6" w="20px" h="20px" borderRadius="" />
          <Text color="#4A5568" m="0 !important" ml="2px !important" fontSize="12px" fontWeight="500">
            {props.title}
          </Text>
        </HStack>
        <Center>
          {props.isLoading ? (
            <BlankSlate size="sm" />
          ) : (
            <Text fontSize="20px" color="gray.600" lineHeight="28px" fontWeight="500">
              {props.type === 'info'
                ? numeral(props.value ? Number(props.value) / 100 : '0').format('0.00%')
                : numeral(props.value ? props.value : '0').format('$0,0.00')}
            </Text>
          )}
        </Center>
      </VStack>
    </>
  )
}

const DollarIcon = () => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14.375" cy="14" r="13.5" fill="#E6F8EE" />
      <path
        d="M16.875 11.8125H18.125C18.125 10.0394 16.4031 9.23063 15 9.04438V7.75H13.75V9.04438C12.3469 9.23063 10.625 10.0394 10.625 11.8125C10.625 13.5038 12.2912 14.3831 13.75 14.5813V17.6875C12.845 17.5306 11.875 17.0475 11.875 16.1875H10.625C10.625 17.8056 12.1406 18.7619 13.75 18.96V20.25H15V18.9563C16.4031 18.77 18.125 17.9606 18.125 16.1875C18.125 14.4144 16.4031 13.6056 15 13.4194V10.3125C15.8312 10.4619 16.875 10.9006 16.875 11.8125V11.8125ZM11.875 11.8125C11.875 10.9006 12.9187 10.4619 13.75 10.3125V13.3119C12.8931 13.1538 11.875 12.6856 11.875 11.8125ZM16.875 16.1875C16.875 17.0994 15.8312 17.5381 15 17.6875V14.6875C15.8312 14.8369 16.875 15.2756 16.875 16.1875V16.1875Z"
        fill="#34C672"
      />
    </svg>
  )
}

const VendorCostIcon = () => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="13.6191" cy="14" r="13.5" fill="#FCEAEB" />
      <path
        d="M19.2441 9H7.99414C7.82838 9 7.66941 9.06585 7.5522 9.18306C7.43499 9.30027 7.36914 9.45924 7.36914 9.625V18.375C7.36914 18.5408 7.43499 18.6997 7.5522 18.8169C7.66941 18.9342 7.82838 19 7.99414 19H19.2441C19.4099 19 19.5689 18.9342 19.6861 18.8169C19.8033 18.6997 19.8691 18.5408 19.8691 18.375V9.625C19.8691 9.45924 19.8033 9.30027 19.6861 9.18306C19.5689 9.06585 19.4099 9 19.2441 9ZM18.6191 15.875C18.1219 15.875 17.6449 16.0725 17.2933 16.4242C16.9417 16.7758 16.7441 17.2527 16.7441 17.75H10.4941C10.4941 17.2527 10.2966 16.7758 9.94497 16.4242C9.59334 16.0725 9.11642 15.875 8.61914 15.875V12.125C9.11642 12.125 9.59334 11.9275 9.94497 11.5758C10.2966 11.2242 10.4941 10.7473 10.4941 10.25H16.7441C16.7441 10.7473 16.9417 11.2242 17.2933 11.5758C17.6449 11.9275 18.1219 12.125 18.6191 12.125V15.875Z"
        fill="#E85659"
      />
    </svg>
  )
}

const ChangeOrderIcon = () => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="13.8086" cy="14" r="13.5" fill="#E4F9FC" />
      <path
        d="M18.748 11.8663C18.7183 11.7984 18.6769 11.7364 18.6255 11.6831L14.8755 7.93312C14.8222 7.88174 14.7602 7.84026 14.6923 7.81062C14.6736 7.80187 14.6536 7.79687 14.6336 7.79C14.5813 7.7722 14.5269 7.76148 14.4717 7.75813C14.4586 7.75688 14.4467 7.75 14.4336 7.75H10.0586C9.36922 7.75 8.80859 8.31062 8.80859 9V19C8.80859 19.6894 9.36922 20.25 10.0586 20.25H17.5586C18.248 20.25 18.8086 19.6894 18.8086 19V12.125C18.8086 12.1119 18.8017 12.1 18.8005 12.0862C18.7974 12.0311 18.7867 11.9766 18.7686 11.9244C18.7623 11.9044 18.7567 11.885 18.748 11.8663ZM16.6748 11.5H15.0586V9.88375L16.6748 11.5ZM10.0586 19V9H13.8086V12.125C13.8086 12.2908 13.8744 12.4497 13.9917 12.5669C14.1089 12.6842 14.2678 12.75 14.4336 12.75H17.5586L17.5598 19H10.0586Z"
        fill="#4CD8EB"
      />
    </svg>
  )
}

const MaterialIcon = () => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14.0605" cy="14" r="13.5" fill="#EEEDFD" />
      <path
        d="M19.0605 8.375H9.06055C8.72903 8.375 8.41108 8.5067 8.17666 8.74112C7.94224 8.97554 7.81055 9.29348 7.81055 9.625V10.875C7.81186 11.0931 7.87025 11.3071 7.97989 11.4957C8.08953 11.6843 8.24662 11.8409 8.43555 11.95V18.375C8.43555 18.7065 8.56724 19.0245 8.80166 19.2589C9.03608 19.4933 9.35403 19.625 9.68555 19.625H18.4355C18.7671 19.625 19.085 19.4933 19.3194 19.2589C19.5539 19.0245 19.6855 18.7065 19.6855 18.375V11.95C19.8745 11.8409 20.0316 11.6843 20.1412 11.4957C20.2508 11.3071 20.3092 11.0931 20.3105 10.875V9.625C20.3105 9.29348 20.1789 8.97554 19.9444 8.74112C19.71 8.5067 19.3921 8.375 19.0605 8.375ZM9.06055 9.625H19.0605V10.875H9.06055V9.625ZM9.68555 18.375V12.125H18.4355V18.375H9.68555Z"
        fill="#746CED"
      />
    </svg>
  )
}

const ProfitIcon = () => {
  return (
    <svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14.1387" cy="13.5" r="13.5" fill="#FFF2DC" />
      <path
        d="M14.7637 9.75H16.0137V16.625H14.7637V9.75ZM17.2637 7.875H18.5137V16.625H17.2637V7.875ZM12.2637 11.625H13.5137V16.625H12.2637V11.625ZM9.13867 17.875H19.1387V19.125H9.13867V17.875ZM9.76367 13.5H11.0137V16.625H9.76367V13.5Z"
        fill="#F6AD55"
      />
    </svg>
  )
}

const ProfitMarginIcon = () => {
  return (
    <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="13.5" cy="13.5" r="13.5" fill="#C4F1F9" />
      <path
        d="M21.332 12.9998C21.1116 13.0014 20.8933 13.0427 20.6876 13.1218L18.4894 11.3223C18.5435 11.1262 18.582 10.9227 18.582 10.7082C18.582 9.43767 17.5609 8.4165 16.2904 8.4165C15.0199 8.4165 13.9987 9.43767 13.9987 10.7082C13.9987 11.0712 14.0904 11.4094 14.2389 11.7138L11.5723 14.8653C11.4655 14.8453 11.3573 14.8345 11.2487 14.8332C11.0846 14.8332 10.9297 14.8607 10.7794 14.901L8.42995 12.5516C8.4712 12.4022 8.4987 12.2473 8.4987 12.0832C8.4987 11.0675 7.68103 10.2498 6.66536 10.2498C5.6497 10.2498 4.83203 11.0675 4.83203 12.0832C4.83203 13.0988 5.6497 13.9165 6.66536 13.9165C6.82945 13.9165 6.98436 13.889 7.1347 13.8487L9.48411 16.1981C9.44005 16.3504 9.41692 16.508 9.41536 16.6665C9.41536 17.6822 10.233 18.4998 11.2487 18.4998C12.2644 18.4998 13.082 17.6822 13.082 16.6665C13.082 16.4493 13.038 16.2448 12.9693 16.0514L15.6395 12.8963C15.8476 12.9568 16.063 12.9998 16.2904 12.9998C16.6521 12.9978 17.0082 12.9095 17.3289 12.7423L19.528 14.5417C19.5108 14.6379 19.501 14.7354 19.4987 14.8332C19.4987 15.8488 20.3164 16.6665 21.332 16.6665C22.3477 16.6665 23.1654 15.8488 23.1654 14.8332C23.1654 13.8175 22.3477 12.9998 21.332 12.9998Z"
        fill="#0BC5EA"
      />
    </svg>
  )
}
